import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { PaymentDefinitionsService } from '@services/payment-definitions.service';
import { ITenantDefinition } from '@shared/interfaces/payment-definitions.interface';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-tenant-definition',
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        DialogModule,
        ToastModule,
        FormsModule,
        ReactiveFormsModule,
        ToggleSwitch,
        ColumnFilterComponent,
        IconField,
        InputIcon,
        PageHeaderComponent
    ],
    templateUrl: './tenant-definition.component.html',
    styleUrl: './tenant-definition.component.scss'
})
export class TenantDefinitionComponent implements OnInit {
    @ViewChild('dt') dt!: Table;

    tenants: ITenantDefinition[] = [];
    loading = true;
    dialogVisible = false;
    isEditMode = false;
    selectedTenant: ITenantDefinition | null = null;
    submitted = false;
    tenantForm!: FormGroup;

    private paymentService = inject(PaymentDefinitionsService);
    private fb = inject(FormBuilder);
    private messageService = inject(MessageService);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.initializeForm();
        this.loadTenants();
    }

    onGlobalSearch(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.dt.filterGlobal(value, 'contains');
    }

    loadTenants(): void {
        this.loading = true;
        this.cd.detectChanges();
        this.paymentService.getTenants().subscribe({
            next: (res) => {
                this.tenants = res.tenantDefinitions || [];
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    initializeForm(): void {
        this.tenantForm = this.fb.group({
            name: ['', [Validators.required, Validators.maxLength(255)]],
            active: [true]
        });
    }

    isInvalid(field: string): boolean {
        const control = this.tenantForm.get(field);
        return !!(control && control.invalid && (this.submitted || control.dirty || control.touched));
    }

    openCreate(): void {
        this.isEditMode = false;
        this.selectedTenant = null;
        this.submitted = false;
        this.tenantForm.reset({ name: '', active: true });
        this.dialogVisible = true;
    }

    openEdit(tenant: ITenantDefinition): void {
        this.isEditMode = true;
        this.selectedTenant = tenant;
        this.submitted = false;
        this.tenantForm.patchValue({
            name: tenant.name,
            active: tenant.active
        });
        this.dialogVisible = true;
    }

    cancelDialog(): void {
        this.dialogVisible = false;
    }

    saveTenant(): void {
        this.submitted = true;
        if (this.tenantForm.invalid) {
            return;
        }

        const formData = this.tenantForm.value;

        if (!this.isEditMode) {
            this.paymentService.createTenant({ name: formData.name }).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tenant has been created successfully.' });
                    this.loadTenants();
                    this.dialogVisible = false;
                }
            });
        } else if (this.selectedTenant) {
            this.paymentService.updateTenant(this.selectedTenant.id, { name: formData.name, active: formData.active }).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tenant has been updated successfully.' });
                    this.loadTenants();
                    this.dialogVisible = false;
                }
            });
        }
    }
}
